import React, { useEffect, useState } from "react";
import { cancelOrder, updateOrderAddress, rescheduleOrder } from "../../api/orderApi";
import { checkPincode } from "../../api/settingsApi";
import { addressLine, PinStatus } from "../AddressFields/AddressFields";
import { formatDeliveryWindow } from "../../utils/checkoutTotals";
import "./OrderActions.css";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const CANCEL_REASONS = [
  "Ordered by mistake",
  "Want to change the product or quantity",
  "Found a better price elsewhere",
  "Need to change the delivery address",
  "Delivery is taking too long",
  "Other",
];

const formatDay = (iso) =>
  new Date(iso).toLocaleDateString("en-IN", { timeZone: "UTC", weekday: "short", day: "numeric", month: "short", year: "numeric" });

// Day choices for rescheduling: tomorrow for the next 10 days.
const dayChoices = () =>
  Array.from({ length: 10 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() + i + 1);
    const pad = (n) => String(n).padStart(2, "0");
    return {
      value: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
      label: d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" }),
    };
  });

// Clear price breakdown: nothing is added after this screen.
export function OrderBreakdown({ order }) {
  const items = Array.isArray(order.orderItems) ? order.orderItems : [];
  const cod = Number(order.codFee) > 0;
  return (
    <div className="oa-breakdown">
      <h4>Order summary</h4>
      {items.map((item) => (
        <div className="oa-row" key={item.id}>
          <span>
            {item.productName} <em>× {item.quantity}</em>
          </span>
          <span>{money(item.lineTotal ?? Number(item.unitPrice) * item.quantity)}</span>
        </div>
      ))}
      <div className="oa-row oa-sep">
        <span>Product Price</span>
        <span>{money(order.subtotal)}</span>
      </div>
      <div className="oa-row">
        <span>Shipping</span>
        <span>{Number(order.shippingFee) > 0 ? money(order.shippingFee) : "FREE"}</span>
      </div>
      {(cod || order.paymentMethod === "cod") && (
        <div className="oa-row">
          <span>COD Fee</span>
          <span>{money(order.codFee)}</span>
        </div>
      )}
      {Number(order.discountAmount) > 0 && (
        <div className="oa-row oa-discount">
          <span>Discount</span>
          <span>− {money(order.discountAmount)}</span>
        </div>
      )}
      {Number(order.prepaidDiscount) > 0 && (
        <div className="oa-row oa-discount">
          <span>Prepaid discount</span>
          <span>− {money(order.prepaidDiscount)}</span>
        </div>
      )}
      <div className="oa-row oa-total">
        <span>Total Payable</span>
        <span>{money(order.total)}</span>
      </div>
      {Number(order.advanceAmount) > 0 && (
        <>
          <div className="oa-row">
            <span>Advance paid online</span>
            <span>{money(order.advanceAmount)}</span>
          </div>
          <div className="oa-row oa-balance">
            <span>Pay on delivery (cash)</span>
            <span>{money(Number(order.total) - Number(order.advanceAmount))}</span>
          </div>
        </>
      )}
    </div>
  );
}

function Modal({ title, onClose, children }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div className="oa-overlay" onClick={onClose}>
      <div className="oa-modal" role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <button type="button" className="oa-close" onClick={onClose} aria-label="Close">
          ×
        </button>
        <h3>{title}</h3>
        {children}
      </div>
    </div>
  );
}

// Delivery details + the self-service buttons for one order.
// props: order, token, onUpdated(updatedOrder)
export default function OrderActions({ order, token, onUpdated }) {
  const [modal, setModal] = useState(null); // "cancel" | "address" | "reschedule"
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [reason, setReason] = useState(CANCEL_REASONS[0]);
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  const [addr, setAddr] = useState({});
  const [pin, setPin] = useState({ status: "idle" });

  const close = () => {
    setModal(null);
    setError("");
  };

  const openAddress = () => {
    const a = order.address || {};
    setAddr({
      fullName: a.fullName || "",
      phone: a.phone || "",
      line1: a.line1 || "",
      line2: a.line2 || "",
      landmark: a.landmark || "",
      city: a.city || "",
      state: a.state || "",
      pincode: a.pincode || "",
    });
    setPin({ status: "idle" });
    setModal("address");
  };

  // Verify the PIN as it is edited, and fill city/state when they're empty.
  useEffect(() => {
    if (modal !== "address") return undefined;
    const code = String(addr.pincode || "").trim();
    if (!/^\d{6}$/.test(code)) {
      setPin({ status: "idle" });
      return undefined;
    }
    let cancelled = false;
    setPin({ status: "checking" });
    checkPincode(code, { cod: order.paymentMethod === "cod" }).then((result) => {
      if (cancelled) return;
      setPin(result);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addr.pincode, modal]);

  const run = async (call, success) => {
    setBusy(true);
    setError("");
    try {
      const result = await call();
      if (!result.ok || !result.success) throw new Error(result.message || "Something went wrong. Please try again.");
      onUpdated(result.data);
      setNotice(success);
      setModal(null);
    } catch (e) {
      setError(e.message || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const a = order.address;
  const paidOnline = order.status === "paid" && order.paymentMethod === "payu";
  const advancePaid = order.status === "paid" && Number(order.advanceAmount) > 0;

  return (
    <div className="oa-wrap">
      {a && (
        <div className="oa-delivery">
          <h4>Delivering to</h4>
          <p>
            <strong>{a.fullName}</strong> · {a.phone}
            <br />
            {addressLine(a)}, {a.city}, {a.state} - {a.pincode}
          </p>
          {formatDeliveryWindow(order.estimatedDeliveryFrom, order.estimatedDeliveryTo) && !["delivered", "cancelled"].includes(order.status) && (
            <p className="oa-pref">
              <strong>{formatDeliveryWindow(order.estimatedDeliveryFrom, order.estimatedDeliveryTo)}</strong>
            </p>
          )}
          {order.paymentMethod === "cod" && !["delivered", "cancelled"].includes(order.status) && (
            <p className={`oa-pref ${order.codConfirmed ? "oa-confirmed" : "oa-awaiting"}`}>
              {order.codConfirmed
                ? "✓ You have confirmed this Cash on Delivery order."
                : "Waiting for your confirmation: please tap the Confirm link we sent you by email / WhatsApp, so we can dispatch it."}
            </p>
          )}
          {order.deliveryPreferredDate && (
            <p className="oa-pref">
              Preferred delivery: <strong>{formatDay(order.deliveryPreferredDate)}</strong>
              {order.deliveryNote ? ` · "${order.deliveryNote}"` : ""}
            </p>
          )}
        </div>
      )}

      {order.status === "cancelled" && (
        <p className="oa-cancelled">
          This order was cancelled{order.cancelReason ? ` (${order.cancelReason})` : ""}.
          {order.refundRequired && " Your refund will be sent to the original payment method within 5-7 working days."}
        </p>
      )}

      {notice && <p className="oa-notice" role="status">{notice}</p>}

      {(order.canCancel || order.canEditAddress || order.canReschedule) && (
        <div className="oa-buttons">
          {order.canReschedule && (
            <button type="button" className="oa-btn" onClick={() => { setDate(""); setNote(""); setModal("reschedule"); }}>
              Reschedule delivery
            </button>
          )}
          {order.canEditAddress && (
            <button type="button" className="oa-btn" onClick={openAddress}>
              Edit delivery address
            </button>
          )}
          {order.canCancel && (
            <button type="button" className="oa-btn oa-btn-danger" onClick={() => setModal("cancel")}>
              Cancel order
            </button>
          )}
        </div>
      )}

      {/* CANCEL */}
      {modal === "cancel" && (
        <Modal title="Cancel this order?" onClose={close}>
          <p className="oa-text">
            You can cancel until the order is dispatched.
            {paidOnline && " Since you paid online, the full amount is refunded to your original payment method within 5-7 working days."}
            {advancePaid && ` Your advance of ${money(order.advanceAmount)} is refunded to your original payment method within 5-7 working days.`}
          </p>
          <label className="oa-field">
            <span>Reason</span>
            <select value={reason} onChange={(e) => setReason(e.target.value)}>
              {CANCEL_REASONS.map((r) => (
                <option key={r}>{r}</option>
              ))}
            </select>
          </label>
          {error && <p className="oa-error" role="alert">{error}</p>}
          <div className="oa-actions">
            <button type="button" className="oa-btn" onClick={close} disabled={busy}>Keep order</button>
            <button
              type="button"
              className="oa-btn oa-btn-danger"
              disabled={busy}
              onClick={() => run(() => cancelOrder(order.id, reason, token), "Your order has been cancelled.")}
            >
              {busy ? "Cancelling..." : "Yes, cancel order"}
            </button>
          </div>
        </Modal>
      )}

      {/* RESCHEDULE */}
      {modal === "reschedule" && (
        <Modal title="Reschedule delivery" onClose={close}>
          <p className="oa-text">Pick the day you will be available to receive the parcel.</p>
          <div className="oa-dates" role="radiogroup" aria-label="New delivery date">
            {dayChoices().map((d) => (
              <button
                type="button"
                key={d.value}
                role="radio"
                aria-checked={date === d.value}
                className={`oa-date ${date === d.value ? "active" : ""}`}
                onClick={() => setDate(d.value)}
              >
                {d.label}
              </button>
            ))}
          </div>
          <label className="oa-field">
            <span>Note for the delivery team (optional)</span>
            <input maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Call before arriving" />
          </label>
          {order.status === "shipped" && (
            <p className="oa-text oa-small">Your parcel is already on its way. We will pass this request on to the courier.</p>
          )}
          {error && <p className="oa-error" role="alert">{error}</p>}
          <div className="oa-actions">
            <button type="button" className="oa-btn" onClick={close} disabled={busy}>Close</button>
            <button
              type="button"
              className="oa-btn oa-btn-gold"
              disabled={busy || !date}
              onClick={() => run(() => rescheduleOrder(order.id, { date, note }, token), "Delivery rescheduled.")}
            >
              {busy ? "Saving..." : "Confirm new date"}
            </button>
          </div>
        </Modal>
      )}

      {/* EDIT ADDRESS */}
      {modal === "address" && (
        <Modal title="Edit delivery address" onClose={close}>
          <form
            className="oa-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (pin.status === "invalid") {
                setError("This PIN code was not found. Please check it.");
                return;
              }
              if (pin.status === "valid" && pin.serviceable === false) {
                setError("Sorry, we cannot deliver to this PIN code yet. Please use a different address.");
                return;
              }
              run(() => updateOrderAddress(order.id, addr, token), "Delivery address updated.");
            }}
          >
            {[
              ["fullName", "Full name", true],
              ["phone", "Phone", true],
              ["line1", "House / flat no.", true],
              ["line2", "Street / area", false],
              ["landmark", "Landmark (optional)", false],
              ["pincode", "PIN code", true],
              ["city", "City", true],
              ["state", "State", true],
            ].map(([key, label, required]) => (
              <label className="oa-field" key={key}>
                <span>{label}</span>
                <input
                  required={required}
                  value={addr[key] || ""}
                  inputMode={key === "pincode" || key === "phone" ? "numeric" : undefined}
                  maxLength={key === "pincode" ? 6 : key === "phone" ? 12 : 255}
                  onChange={(e) => setAddr((prev) => ({ ...prev, [key]: e.target.value }))}
                />
                {key === "pincode" && <PinStatus pinCheck={pin} />}
              </label>
            ))}
            {error && <p className="oa-error" role="alert">{error}</p>}
            <div className="oa-actions">
              <button type="button" className="oa-btn" onClick={close} disabled={busy}>Close</button>
              <button type="submit" className="oa-btn oa-btn-gold" disabled={busy}>
                {busy ? "Saving..." : "Save address"}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
